import React from 'react'
import useIsMobile from '../../../../../services/community/shared/useIsMobile'
import PlaylistGridSkeleton from './PlaylistGridSkeleton'
import TileV2Skeleton from './TileV2Skeleton'

/**
 * Skeleton state for playlist tile rows during content hydration.
 *
 * Rendered by row components (MyPlaylistRow, SystemPlaylistRow,
 * ProfileFollowingTab row) when the shell query has settled but
 * `useContentHydration` is still resolving Content for the row's tiles.
 * Prevents the "No episodes yet" → real-tiles layout shift that surfaces
 * during the 100–400ms hydration window on cache-miss visits.
 *
 * Viewport-aware (mirrors `ResponsivePlaylistDisplay` in
 * CommunityProfileCarousels.tsx):
 *
 *   • Mobile  (< 768px): delegates to <PlaylistGridSkeleton>, which renders
 *     into the `.playlist-tile-grid` block (2 → 3 → 4 column responsive grid).
 *     Mirrors the real `<PlaylistTileGrid>` layout that follows.
 *
 *   • Desktop (≥ 768px): renders `row-v2 row-v2--s12345 row-v2--disable-scale`
 *     markup that mirrors the real Row.v2 output (Row.jsx lines 629–651).
 *     This reuses the existing `.row-v2--s12345 .row-v2__item` width rules
 *     in `_Row.scss` lines 349–371 for the 1 → 2 → 3 → 4 → 5-col layout
 *     without touching shared SCSS.
 *
 * SSR note: `useIsMobile` returns `false` until the first effect runs, so
 * the very first paint is desktop markup.  This matches the real
 * `ResponsivePlaylistDisplay` behaviour, so each viewport's skeleton →
 * real-tile transition stays reflow-free.
 *
 * The series-label corner-badge placeholder is opt-in via `showSeriesLabel`.
 * When enabled, every 3rd cell renders a badge to preview mixed
 * series/non-series rows; when disabled (the default), no skeleton tile
 * renders a corner badge.  Today only the Currently Watching surfaces opt
 * in — every other playlist's skeleton should stay badge-free.
 *
 * `role="presentation"` + `aria-hidden` keeps screen readers silent until
 * the real tiles announce themselves.
 */
interface PlaylistItemCarouselSkeletonProps {
  /** How many skeleton cells to render.  Cap upstream — a 200-item
   *  playlist must not paint 200 ghost cells. */
  count: number
  /** Opt in to the series-label corner-badge placeholder on every 3rd tile.
   *  Defaults to `false` — only Currently Watching surfaces pass `true`. */
  showSeriesLabel?: boolean
}

/** Desktop carousel-shaped skeleton cap.  s12345 shows up to 5 tiles at
 *  widescreen; 6 gives a sliver of the partially-visible 6th cell so the
 *  edge of the row reads as scrollable. */
const DESKTOP_CAP = 6

function PlaylistItemCarouselSkeleton ({
  count,
  showSeriesLabel = false,
}: PlaylistItemCarouselSkeletonProps): JSX.Element {
  const isMobile = useIsMobile()

  if (isMobile) {
    return <PlaylistGridSkeleton count={count} showSeriesLabel={showSeriesLabel} />
  }

  const safeCount = Math.min(Math.max(0, Math.floor(count)), DESKTOP_CAP)
  return (
    <section
      // Mirror the real Row.v2 root classes so the existing
      // `.row-v2--s12345 .row-v2__item` width rules give us the same
      // 1 → 2 → 3 → 4 → 5-col layout per breakpoint.  `--disable-scale`
      // matches the real consumer (CommunityProfileCarousels.tsx line 144),
      // suppressing hover transforms and shadows that the skeleton has no
      // use for.  `community-row-skeleton` is a community-scoped hook for
      // any future tweaks without touching shared `_Row.scss`.
      className="row-v2 row-v2--s12345 row-v2--disable-scale community-row-skeleton"
      role="presentation"
      aria-hidden
    >
      <div className="row-v2__wrapper">
        <div className="row-v2__content">
          <ul className="row-v2__list">
            {Array.from({ length: safeCount }, (_, idx) => (
              <li
                // Static composite key — the array is fixed-length per render
                // and never reorders, so the index is safe; embedding
                // `safeCount` keeps keys stable across count changes without
                // tripping the `react/no-array-index-key` lint.
                key={`playlist-carousel-skeleton-${safeCount}-${idx}`}
                className="row-v2__item"
              >
                <div className="row-v2__item-inner row-v2__item-inner--no-shadow">
                  <TileV2Skeleton showSeriesLabel={showSeriesLabel && idx % 3 === 0} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default React.memo(PlaylistItemCarouselSkeleton)
